import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { Checkbox } from '../../../components/ui/Checkbox';

const OrderAnalyticsTable = () => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  
  const [orders] = useState([
    {
      id: "ORD-2025-001234",
      customer: "Sarah Johnson",
      service: "Home Cleaning",
      provider: "CleanPro Services",
      amount: 89.99,
      status: "completed",
      paymentMethod: "Credit Card",
      location: "Downtown, NYC",
      createdAt: "2025-10-27 10:30",
      completedAt: "2025-10-27 14:30",
      vertical: "Services"
    },
    {
      id: "ORD-2025-001235",
      customer: "Michael Chen",
      service: "Food Delivery",
      provider: "QuickBite Restaurant",
      amount: 34.50,
      status: "completed",
      paymentMethod: "Digital Wallet",
      location: "Midtown, NYC",
      createdAt: "2025-10-27 13:15",
      completedAt: "2025-10-27 14:25",
      vertical: "Food"
    },
    {
      id: "ORD-2025-001236",
      customer: "Emily Rodriguez",
      service: "Property Viewing",
      provider: "Elite Realty Group",
      amount: 0.00,
      status: "disputed",
      paymentMethod: "N/A",
      location: "Brooklyn, NYC",
      createdAt: "2025-10-27 09:00",
      completedAt: null,
      vertical: "Property"
    },
    {
      id: "ORD-2025-001237",
      customer: "James Wilson",
      service: "City Tour",
      provider: "NYC Explorer Tours",
      amount: 149.99,
      status: "in_progress",
      paymentMethod: "Credit Card",
      location: "Manhattan, NYC",
      createdAt: "2025-10-27 11:00",
      completedAt: null,
      vertical: "Tours"
    },
    {
      id: "ORD-2025-001238",
      customer: "Anna Martinez",
      service: "Plumbing Repair",
      provider: "FixIt Pro Services",
      amount: 175.00,
      status: "active",
      paymentMethod: "Bank Transfer",
      location: "Queens, NYC",
      createdAt: "2025-10-27 08:45",
      completedAt: null,
      vertical: "Services"
    }
  ]);

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
      completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
      disputed: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[status] || colors?.active;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Active',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      disputed: 'Disputed'
    };
    return labels?.[status] || 'Unknown';
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(orders?.map(order => order?.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId, checked) => {
    if (checked) {
      setSelectedOrders([...selectedOrders, orderId]);
    } else {
      setSelectedOrders(selectedOrders?.filter(id => id !== orderId));
    }
  };

  const sortedOrders = [...orders]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const SortableHeader = ({ children, sortKey }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-hover"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <Icon 
          name={sortConfig?.key === sortKey 
            ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown')
            : 'ChevronsUpDown'
          } 
          size={14} 
        />
      </div>
    </th>
  );

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Table Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Order Analytics</h3>
          <div className="flex items-center space-x-2">
            {selectedOrders?.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  {selectedOrders?.length} selected
                </span>
                <Button variant="outline" size="sm" iconName="Download">
                  Export Selected
                </Button>
                <Button variant="outline" size="sm" iconName="Edit">
                  Bulk Actions
                </Button>
              </div>
            )}
            <Button variant="outline" size="sm" iconName="Filter">
              Advanced Filter
            </Button>
            <Button variant="outline" size="sm" iconName="Download">
              Export All
            </Button>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={selectedOrders?.length === orders?.length}
                  indeterminate={selectedOrders?.length > 0 && selectedOrders?.length < orders?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <SortableHeader sortKey="id">Order ID</SortableHeader>
              <SortableHeader sortKey="customer">Customer</SortableHeader>
              <SortableHeader sortKey="service">Service</SortableHeader>
              <SortableHeader sortKey="provider">Provider</SortableHeader>
              <SortableHeader sortKey="amount">Amount</SortableHeader>
              <SortableHeader sortKey="status">Status</SortableHeader>
              <SortableHeader sortKey="vertical">Vertical</SortableHeader>
              <SortableHeader sortKey="createdAt">Created</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedOrders?.map((order) => (
              <tr key={order?.id} className="hover:bg-muted/50 transition-hover">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Checkbox
                    checked={selectedOrders?.includes(order?.id)}
                    onChange={(e) => handleSelectOrder(order?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">{order?.id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{order?.customer}</div>
                  <div className="text-sm text-muted-foreground">{order?.location}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{order?.service}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{order?.provider}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">
                    ${order?.amount?.toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground">{order?.paymentMethod}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(order?.status)}`}>
                    {getStatusLabel(order?.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{order?.vertical}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{order?.createdAt}</div>
                  {order?.completedAt && (
                    <div className="text-sm text-muted-foreground">Completed: {order?.completedAt}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="View Details">
                      <Icon name="Eye" size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Contact Customer">
                      <Icon name="Phone" size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="More Actions">
                      <Icon name="MoreHorizontal" size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className="px-6 py-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing 1 to {orders?.length} of {orders?.length} results
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm" className="bg-primary text-primary-foreground">
              1
            </Button>
            <Button variant="outline" size="sm" disabled>
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderAnalyticsTable;